import type { ReactNode } from 'react'
/**
 * The non-populated list states. Every list renders all four — loading, empty,
 * error, populated — and the empty state is the first screen a new account
 * ever sees, so it gets real copy rather than a shrug.
 */

export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <ul aria-busy="true" aria-live="polite">
      <li className="sr-only">Loading…</li>
      {Array.from({ length: rows }, (_, index) => (
        <li
          key={index}
          className="border-border flex min-h-11 items-center gap-3 border-b py-1.5 sm:min-h-9"
        >
          <div className="bg-muted size-[18px] shrink-0 animate-pulse rounded-full" />
          <div className="bg-muted h-3.5 w-2/5 animate-pulse rounded" />
        </li>
      ))}
    </ul>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  /** Somewhere to go next, when the empty state knows of one. */
  action?: ReactNode
}) {
  return (
    <div className="py-10 text-center">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-muted-foreground mt-1 text-sm">{description}</p>
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div role="alert" className="py-10 text-center">
      <p className="text-sm font-medium">Something went wrong</p>
      <p className="text-muted-foreground mt-1 text-sm">
        The data could not be loaded.
      </p>
      {onRetry !== undefined && (
        <button
          type="button"
          onClick={onRetry}
          className="text-primary mt-4 min-h-11 text-sm underline underline-offset-4"
        >
          Try again
        </button>
      )}
    </div>
  )
}
